import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { catchError, map, of, switchMap } from "rxjs";
import { environment } from "../../../enviroments/enviroment";

const consentText = `Wyrażam zgodę na przetwarzanie przez NiceProject Sp. z o.o. z siedzibą we Wrocławiu
    przy ul. Powstańców Śląskich, 2-4, 53-333 Wrocław, Polska, moich danych osobowych zawartych w formularzu
    aplikacyjnym oraz w dostarczonych przeze mnie dokumentach aplikacyjnych (w tym w moim CV) dla potrzeb
    niezbędnych do przeprowadzenia bieżącego procesu rekrutacji.`;

export type Career = {
  agreementFirst: boolean | null;
  agreementSecond: boolean | null;
  cv: File;
  email: string;
  job: string;
  additionalMessage: string;
  name: string;
};

export type MessageState = {
  show: boolean;
  state: string;
};

export const FORM_INITIAL_VALUE = {
  name: "",
  email: "",
  phoneNumber: "",
  message: "",
  fileUpload: "",
  agreement: undefined,
  captcha: false,
};

export type JobInfo = {
  jobUrl: string;
  jobTitle: string;
};

export type FormValue = typeof FORM_INITIAL_VALUE & Partial<JobInfo>;

export type FormType = "job-application" | "contact";

@Injectable({
  providedIn: "root",
})
export class PostService {
  private readonly http = inject(HttpClient);

  public sendMessage(data: FormValue, file: File | null, formType: FormType) {
    return formType === "job-application"
      ? this.sendCareerForm(data, file)
      : this.sendContactForm(data, file);
  }

  public sendCareerForm(data: FormValue, file: File | null) {
    const body = {
      ...this.getBasicBody(data),
      agreementFirst: data.agreement ? consentText : "",
      agreementSecond: "",
      job: data.jobTitle,
      jobUrl: data.jobUrl,
      additionalMessage: data.message,
    };

    return this.sendForm(body, file, "job-application");
  }

  public sendContactForm(data: FormValue, file: File | null) {
    const body = {
      ...this.getBasicBody(data),
      message: data.message,
    };

    return this.sendForm(body, file, "contact");
  }

  private getBasicBody(data: FormValue) {
    return {
      email: data.email,
      name: data.name,
      phoneNumber: data.phoneNumber ? data.phoneNumber : "-",
      filename: "",
    };
  }

  private sendForm(
    body: Record<string, unknown>,
    file: File | null,
    formType: FormType,
  ) {
    const sendMessageUrl = `${environment.apiGatewayUrl}/send-message?form=${formType}`;
    const headers = { "x-api-key": environment.apiGatewayKey };

    if (file) {
      return this.http
        .get<{ uploadURL: string; key: string }>(
          `${environment.apiGatewayUrl}/get-upload-url?filename=${file.name}`,
          { headers },
        )
        .pipe(
          switchMap(({ uploadURL, key }) => {
            const formData = new FormData();
            formData.append("file", file, key);

            return this.http
              .put(uploadURL, formData, {
                headers: { "Content-Type": "multipart/form-data" },
              })
              .pipe(
                switchMap(() => {
                  body["filename"] = key;
                  return this.http.post(sendMessageUrl, body, { headers });
                }),
              );
          }),
          map(() => true),
          catchError(() => of(false)),
        );
    }

    return this.http
      .post(sendMessageUrl, body, { headers })
      .pipe(map(() => true));
  }
}
